/**
 * UNE FIGURE v2 ET UN TRACÉ, EN SVG, POUR LA CONSOLE — tranche T2 du chantier
 * « mathématiques de bout en bout » : le travail se retrouve tel qu'il a été
 * fait.
 *
 * ─── Le moteur est celui du produit, pas un second ──────────────────────────
 *
 * La console ne dessinait AUCUNE figure v2 : la figure d'une fiche se lisait
 * « Figure tracée dans l'application — non dessinée dans la console », celle
 * d'un énoncé n'arrivait pas, et le tracé d'un élève se réduisait à une phrase.
 * Elle ne gagne pas pour autant un moteur de dessin : ce module appelle ceux
 * de `@silex/shared` — `figureVersSvg` (une figure de la banque) et
 * `superposerLesTraces` puis `listeVersSvg` (le tracé d'un élève et le tracé
 * attendu) —, les mêmes que l'application et que la copie imprimable. Il ne
 * décide d'aucune position : il choisit des COULEURS, et rien d'autre.
 *
 * ─── Aucune couleur littérale ───────────────────────────────────────────────
 *
 * Chaque rôle de la palette est une variable de `theme.css` : la figure suit
 * le thème d'accent et le mode sombre, comme le reste de la console. Jamais de
 * rouge : l'écart est en ardoise, comme partout chez le tuteur.
 *
 * ─── Ce qui sort d'ici ──────────────────────────────────────────────────────
 *
 * Une CHAÎNE `<svg>…</svg>`, dont tout texte est échappé par le moteur
 * (`echapperXml`, attributs compris). `FigureV2Web` est le seul composant qui
 * la pose dans la page.
 */
import {
  figureVersSvg,
  listeVersSvg,
  normaliserFigureV2,
  resoudreFigureDeFiche,
  superposerLesTraces,
  type FigureV2Fiche,
  type FigureV2Plan,
  type PalettePartielleSvg,
  type TraceDeTravailDto,
  type TraceDessin,
  type ViewBoxAffichage,
} from '@silex/shared';

/** Les rôles d'une figure, en variables du thème de la console. */
export const PALETTE_CONSOLE: PalettePartielleSvg = {
  calque: {
    grille: 'var(--ligne)',
    axe: 'var(--muted)',
    figure: 'var(--acc)',
    courbe: 'var(--encre)',
    cote: 'var(--muted)',
  },
  cache: 'var(--muted)',
  remplissage: 'var(--acc)',
  marque: 'var(--encre)',
  /* TEXTE d'accent : `--acc-ink`, jamais `--acc` seul (contraste, spec § 1.2). */
  texte: {
    nom: 'var(--acc-ink)',
    etiquette: 'var(--encre)',
    cote: 'var(--muted)',
    graduation: 'var(--muted)',
  },
  halo: 'var(--card)',
  tons: {
    enonce: 'var(--muted)',
    eleve: 'var(--acc)',
    attendu: 'var(--encre)',
    ecart: 'var(--attention)',
  },
};

export type SvgDeConsole =
  | { readonly svg: string }
  /** La figure ne se dessine pas : pourquoi. Jamais un dessin à moitié. */
  | { readonly griefs: readonly string[] };

/** Une figure v2 brute (celle d'un énoncé, d'une fiche) en `<svg>`. */
export function svgDeFigure(figure: unknown, titre: string): SvgDeConsole {
  const r = figureVersSvg(figure, { couleurs: PALETTE_CONSOLE, classe: 'figure-v2__svg', titre });
  return 'svg' in r ? { svg: r.svg } : { griefs: r.griefs };
}

/**
 * La figure v2 d'une FICHE et ce que le prof écrit dessus (`CAP-06`). Le
 * `<svg>` de la figure prend le cadre COMMUN aux deux couches ; les annotations
 * sont rendues en traces, que `DessinWeb` sait peindre, avec ce même cadre.
 * Sans annotation : exactement `svgDeFigure`.
 */
export function svgDeFigureDeFiche(
  illustration: Pick<FigureV2Fiche, 'figure' | 'annotations' | 'decrit'>,
):
  | {
      readonly svg: string;
      readonly cadre: ViewBoxAffichage;
      readonly traces: readonly TraceDessin[];
      readonly griefs: readonly string[];
    }
  | { readonly griefs: readonly string[] } {
  const resolu = resoudreFigureDeFiche(illustration);
  if (resolu === null) {
    const r = normaliserFigureV2(illustration.figure);
    return { griefs: 'griefs' in r ? r.griefs : ['figure illisible'] };
  }
  return {
    svg: listeVersSvg(resolu.liste, {
      couleurs: PALETTE_CONSOLE,
      classe: 'figure-v2__svg',
      titre: illustration.decrit || 'Figure',
    }),
    cadre: resolu.liste.viewBox,
    traces: resolu.traces,
    griefs: resolu.griefs,
  };
}

/** La figure d'énoncé d'un tracé, normalisée, si c'est un plan ; `null` sinon — ou sans figure. */
export function planDEnonce(figure: unknown): FigureV2Plan | null {
  if (figure === undefined || figure === null) return null;
  const r = normaliserFigureV2(figure);
  return 'figure' in r && r.figure.espace === 'plan' ? r.figure : null;
}

/**
 * Le tracé de l'élève et le tracé attendu, superposés sur le repère — et sur
 * la figure de l'énoncé quand l'essai la garde. La règle de superposition est
 * celle de l'application (`superposerLesTraces`).
 */
export function svgDesTraces(
  trace: Pick<TraceDeTravailDto, 'eleve' | 'attendu'>,
  figureDEnonce: unknown,
): { readonly svg: string; readonly description: string } {
  const { liste, description } = superposerLesTraces(
    trace.eleve,
    trace.attendu,
    planDEnonce(figureDEnonce),
  );
  /* La description du moteur parle à l'élève (« Ton tracé. ») : ici, c'est son
     tuteur qui lit. */
  const pourLeTuteur = description.replace(/^Ton tracé\./, 'Le tracé de l’élève.');
  return {
    svg: listeVersSvg(liste, {
      couleurs: PALETTE_CONSOLE,
      classe: 'figure-v2__svg',
      titre: pourLeTuteur,
    }),
    description: pourLeTuteur,
  };
}

/** L'adresse d'une page au stylet, à poser dans un `<img>` : une image PNG en ligne. */
export function adresseDeLaPage(page: {
  readonly imageBase64: string;
  readonly mimeType: string;
}): string {
  return `data:${page.mimeType};base64,${page.imageBase64}`;
}
